import React, { useState, useEffect, useMemo, useRef } from 'react';
import SquareCardForm from '../SquareCardForm';
import styles from './BookSession.module.css';

const TOKEN_KEY = 'ngs_parent_token';
function authHeader() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtMoney(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}
function fmtDateLong(iso) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function BookSession({ onBooked }) {
  // ── Data bundle
  const [data, setData] = useState(null);
  const [dataError, setDataError] = useState(null);

  // ── Wizard state
  const [step, setStep] = useState('service'); // service → slot → review → confirm
  const [variationKey, setVariationKey] = useState(null);
  const [teamMemberId, setTeamMemberId] = useState(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // ── Payment state
  const [paymentMethod, setPaymentMethod] = useState(null); // 'balance' | 'card'
  const [note, setNote] = useState('');
  const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);
  const cardRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/parent/square/booking-data', { headers: authHeader() });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to load booking data');
        setData(json);
        // Default to first bookable staff — Square requires a specific teamMemberId,
        // so "Any available" cannot be the default selection.
        if (json.staff?.length > 0) setTeamMemberId(json.staff[0].teamMemberId);
        // Default: preselect all of this parent's athletes; parent can untoggle
        if (Array.isArray(json.athletes) && json.athletes.length > 0) {
          setSelectedAthleteIds(json.athletes.map(a => a.id));
        }
      } catch (e) {
        setDataError(e.message);
      }
    })();
  }, []);

  // Flatten variations for selection
  const flatVariations = useMemo(() => {
    if (!data) return [];
    const list = [];
    (data.services || []).forEach(s => {
      (s.variations || []).forEach(v => {
        list.push({
          key: v.id,
          serviceId: s.id,
          serviceName: s.name,
          description: s.description,
          variationName: v.name,
          version: v.version,
          priceCents: v.priceCents,
          durationMs: v.serviceDurationMs,
        });
      });
    });
    return list;
  }, [data]);

  const selected = flatVariations.find(v => v.key === variationKey);
  const balance = data?.client?.balance || 0;
  const perAthleteCents = selected?.priceCents || 0;
  const athleteCount = Math.max(1, selectedAthleteIds.length);
  const priceCents = perAthleteCents * athleteCount;
  const balanceOk = priceCents > 0 && balance * 100 >= priceCents;
  const selectedAthletes = (data?.athletes || []).filter(a => selectedAthleteIds.includes(a.id));

  // ── Step 2: fetch availability when user reaches slot step
  useEffect(() => {
    if (step !== 'slot' || !selected || !data?.location?.id) return;
    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSlotsError(null);
      setSlots(null);
      setSelectedSlot(null);
      try {
        const startAt = new Date(`${date}T00:00:00`).toISOString();
        const endDate = new Date(`${date}T00:00:00`);
        endDate.setDate(endDate.getDate() + 1);
        endDate.setHours(endDate.getHours() + 1);
        const endAt = endDate.toISOString();

        const params = new URLSearchParams({
          serviceVariationId: selected.key,
          locationId: data.location.id,
          startAt,
          endAt,
        });
        if (teamMemberId) params.set('teamMemberId', teamMemberId);

        const res = await fetch(`/api/parent/square/availability?${params.toString()}`, { headers: authHeader() });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(json.error || 'Availability search failed');
        // Filter slots to the local NGS date (America/Denver). Using
        // toISOString().slice(0,10) compares UTC dates, which drops any slot
        // after ~6pm local — those roll to the next UTC day and would be
        // filtered out incorrectly.
        const localDateFmt = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit',
        });
        const onDate = (json.availabilities || []).filter(a => {
          return localDateFmt.format(new Date(a.startAt)) === date;
        });
        setSlots(onDate);
      } catch (e) {
        if (!cancelled) setSlotsError(e.message);
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, variationKey, teamMemberId, date, data?.location?.id]);

  const submitBooking = async () => {
    const missing = [];
    if (!selected) missing.push('service');
    if (!selectedSlot) missing.push('time slot');
    if (!teamMemberId) missing.push('trainer');
    if (!data?.location?.id) missing.push('location');
    if (missing.length) {
      setSubmitError(`Missing: ${missing.join(', ')}. Please go back and complete all steps.`);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payment = { method: paymentMethod, amountCents: priceCents };
      if (paymentMethod === 'card') {
        payment.sourceId = await cardRef.current.tokenize();
      }

      const durationMinutes = selected.durationMs ? Math.round(selected.durationMs / 60000) : 30;
      const body = {
        serviceVariationId: selected.key,
        serviceVariationVersion: selected.version,
        teamMemberId,
        locationId: data.location.id,
        startAt: selectedSlot.startAt,
        durationMinutes,
        customerNote: note,
        serviceName: selected.serviceName,
        payment,
        athleteIds: selectedAthleteIds,
      };

      const res = await fetch('/api/parent/square/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Booking failed');
      setResult(json);
      setStep('confirm');
      onBooked?.();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render states
  if (dataError) {
    return <div className={styles.errorCard}>Couldn't load booking data: {dataError}</div>;
  }
  if (!data) {
    return <div className={styles.loadingCard}>Loading booking options…</div>;
  }
  if (flatVariations.length === 0) {
    return <div className={styles.errorCard}>No bookable services are available right now. Please contact Michael directly.</div>;
  }

  // ── Confirmation screen
  if (step === 'confirm' && result) {
    return (
      <div className={styles.confirmCard}>
        <div className={styles.confirmCheck}>✓</div>
        <div className={styles.confirmTitle}>You're booked!</div>
        <div className={styles.confirmSub}>A confirmation has been sent to {data.client.email}.</div>
        <div className={styles.confirmDetails}>
          {selectedAthletes.length > 0 && (
            <div className={styles.detailRow}>
              <span>{selectedAthletes.length > 1 ? 'Athletes' : 'Athlete'}</span>
              <strong>{selectedAthletes.map(a => a.name).join(' & ')}</strong>
            </div>
          )}
          <div className={styles.detailRow}><span>Session</span><strong>{selected.serviceName}</strong></div>
          <div className={styles.detailRow}><span>Date</span><strong>{fmtDateLong(result.booking.startAt)}</strong></div>
          <div className={styles.detailRow}><span>Time</span><strong>{fmtTime(result.booking.startAt)}</strong></div>
          {paymentMethod === 'card' && result.payment && (
            <div className={styles.detailRow}><span>Total paid by card</span><strong>{fmtMoney(priceCents)}</strong></div>
          )}
          {paymentMethod === 'balance' && (
            <>
              <div className={styles.detailRow}><span>Total deducted from balance</span><strong>{fmtMoney(priceCents)}</strong></div>
              <div className={styles.detailRow}><span>Remaining balance</span><strong>${Number(result.newBalance).toFixed(2)}</strong></div>
            </>
          )}
        </div>
        <button className={styles.primaryBtn} onClick={() => {
          setResult(null); setStep('service'); setVariationKey(null); setSelectedSlot(null);
          setPaymentMethod(null); setNote('');
          setSelectedAthleteIds(data.athletes?.map(a => a.id) || []);
        }}>Book Another Session</button>
      </div>
    );
  }

  // ── Step indicator
  const stepIndex = ['service', 'slot', 'review'].indexOf(step);
  const steps = [
    { key: 'service', label: 'Choose Session' },
    { key: 'slot', label: 'Pick Time' },
    { key: 'review', label: 'Confirm' },
  ];

  return (
    <div className={styles.card}>
      <div className={styles.stepper}>
        {steps.map((s, i) => (
          <div key={s.key} className={`${styles.stepItem} ${i <= stepIndex ? styles.stepActive : ''}`}>
            <div className={styles.stepDot}>{i + 1}</div>
            <div className={styles.stepLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Step 1: Service */}
      {step === 'service' && (
        <>
          <h2 className={styles.stepTitle}>Choose a Session</h2>
          <div className={styles.serviceGrid}>
            {flatVariations.map(v => {
              const isSelected = v.key === variationKey;
              return (
                <button
                  key={v.key}
                  className={`${styles.serviceCard} ${isSelected ? styles.serviceCardActive : ''}`}
                  onClick={() => setVariationKey(v.key)}
                >
                  <div className={styles.serviceName}>{v.serviceName}</div>
                  {v.variationName && v.variationName !== 'Regular' && (
                    <div className={styles.serviceVariation}>{v.variationName}</div>
                  )}
                  {v.description && <div className={styles.serviceDesc}>{v.description}</div>}
                  <div className={styles.serviceMeta}>
                    {v.durationMs ? `${Math.round(v.durationMs / 60000)} min` : ''}
                    {v.priceCents != null ? <span className={styles.servicePrice}>{fmtMoney(v.priceCents)}</span> : null}
                  </div>
                </button>
              );
            })}
          </div>

          {data.athletes?.length > 0 && (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel}>
                Who is training?
                {data.athletes.length > 1 && <span style={{ color: '#888', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}> · tap to toggle (each athlete is billed at the session rate)</span>}
              </label>
              <div className={styles.athleteChips}>
                {data.athletes.map(a => {
                  const on = selectedAthleteIds.includes(a.id);
                  return (
                    <button key={a.id} type="button"
                      className={`${styles.athleteChip} ${on ? styles.athleteChipOn : ''}`}
                      onClick={() => setSelectedAthleteIds(prev =>
                        on ? prev.filter(x => x !== a.id) : [...prev, a.id]
                      )}>
                      {on ? '✓ ' : ''}{a.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {data.staff.length > 1 && (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel}>Trainer</label>
              <select className={styles.select} value={teamMemberId || ''} onChange={e => setTeamMemberId(e.target.value || null)}>
                {data.staff.map(s => <option key={s.teamMemberId} value={s.teamMemberId}>{s.displayName}</option>)}
              </select>
            </div>
          )}

          <div className={styles.actions}>
            <button
              className={styles.primaryBtn}
              disabled={!variationKey || (data.athletes?.length > 0 && selectedAthleteIds.length === 0)}
              onClick={() => setStep('slot')}
            >
              Next: Pick a Time →
            </button>
          </div>
        </>
      )}

      {/* ── Step 2: Slot */}
      {step === 'slot' && (
        <>
          <h2 className={styles.stepTitle}>Pick a Time</h2>
          <div className={styles.sessionSummary}>
            {selected.serviceName} · {selected.durationMs ? `${Math.round(selected.durationMs / 60000)} min` : ''} · {fmtMoney(selected.priceCents)}
          </div>

          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel}>Date</label>
            <input type="date" className={styles.select} value={date} onChange={e => setDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
          </div>

          {slotsLoading && <div className={styles.loading}>Loading available times…</div>}
          {slotsError && <div className={styles.errorInline}>{slotsError}</div>}
          {slots && slots.length === 0 && !slotsLoading && (
            <div className={styles.emptyNotice}>
              No open times on this date. Try another date or contact Michael directly.
            </div>
          )}
          {slots && slots.length > 0 && (
            <div className={styles.slotGrid}>
              {slots.map((s, i) => (
                <button
                  key={i}
                  className={`${styles.slot} ${selectedSlot?.startAt === s.startAt ? styles.slotActive : ''}`}
                  onClick={() => setSelectedSlot(s)}
                >
                  {fmtTime(s.startAt)}
                </button>
              ))}
            </div>
          )}

          <div className={styles.actions}>
            <button className={styles.secondaryBtn} onClick={() => setStep('service')}>← Back</button>
            <button className={styles.primaryBtn} disabled={!selectedSlot} onClick={() => setStep('review')}>
              Next: Confirm →
            </button>
          </div>
        </>
      )}

      {/* ── Step 3: Review + payment */}
      {step === 'review' && selected && selectedSlot && (
        <>
          <h2 className={styles.stepTitle}>Confirm &amp; Pay</h2>

          <div className={styles.reviewBox}>
            <div className={styles.reviewRow}><span>Session</span><strong>{selected.serviceName}</strong></div>
            <div className={styles.reviewRow}><span>Date</span><strong>{fmtDateLong(selectedSlot.startAt)}</strong></div>
            <div className={styles.reviewRow}><span>Time</span><strong>{fmtTime(selectedSlot.startAt)}</strong></div>
            <div className={styles.reviewRow}><span>Duration</span><strong>{selected.durationMs ? `${Math.round(selected.durationMs / 60000)} min` : '—'}</strong></div>
            {selectedAthletes.length > 0 && perAthleteCents > 0 ? (
              <>
                {selectedAthletes.map(a => (
                  <div key={a.id} className={styles.reviewRow}>
                    <span>{a.name}</span><strong>{fmtMoney(perAthleteCents)}</strong>
                  </div>
                ))}
                <div className={`${styles.reviewRow} ${styles.reviewTotal}`}>
                  <span>Total</span><strong>{fmtMoney(priceCents)}</strong>
                </div>
              </>
            ) : (
              <div className={`${styles.reviewRow} ${styles.reviewTotal}`}><span>Total</span><strong>{fmtMoney(priceCents)}</strong></div>
            )}
          </div>

          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel}>Note to Michael (optional)</label>
            <textarea className={styles.textarea} rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Anything Michael should know…" />
          </div>

          <label className={styles.fieldLabel} style={{ marginTop: 18 }}>Payment Method</label>
          <div className={styles.payOptions}>
            <button
              className={`${styles.payOption} ${paymentMethod === 'balance' ? styles.payOptionActive : ''} ${!balanceOk ? styles.payOptionDisabled : ''}`}
              onClick={() => balanceOk && setPaymentMethod('balance')}
              disabled={!balanceOk}
            >
              <div className={styles.payOptionTitle}>Account Balance</div>
              <div className={styles.payOptionSub}>
                Current: ${balance.toFixed(2)}
                {!balanceOk && <span className={styles.payOptionWarn}> · insufficient</span>}
              </div>
            </button>
            <button
              className={`${styles.payOption} ${paymentMethod === 'card' ? styles.payOptionActive : ''}`}
              onClick={() => setPaymentMethod('card')}
            >
              <div className={styles.payOptionTitle}>Pay by Card</div>
              <div className={styles.payOptionSub}>Secure payment via Square</div>
            </button>
          </div>

          {paymentMethod === 'card' && (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel}>Card Details</label>
              <SquareCardForm ref={cardRef} config={data.payments} />
            </div>
          )}

          {submitError && <div className={styles.errorInline}>{submitError}</div>}

          <div className={styles.actions}>
            <button className={styles.secondaryBtn} onClick={() => setStep('slot')} disabled={submitting}>← Back</button>
            <button
              className={styles.primaryBtn}
              disabled={!paymentMethod || submitting}
              onClick={submitBooking}
            >
              {submitting ? 'Booking…' : paymentMethod === 'balance' ? `Book & Deduct ${fmtMoney(priceCents)}` : paymentMethod === 'card' ? `Book & Pay ${fmtMoney(priceCents)}` : 'Book Session'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
